import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $insertNodeToNearestRoot, mergeRegister } from "@lexical/utils"
import {
  $createParagraphNode,
  $getNodeByKey,
  COMMAND_PRIORITY_EDITOR,
  createCommand
} from "lexical"
import { useEffect } from "react"

import {
  $createLayoutContainerNode,
  $isLayoutContainerNode,
  LayoutContainerNode
} from "../../nodes/LayoutContainerNode"
import {
  $createLayoutItemNode,
  $isLayoutItemNode,
  LayoutItemNode
} from "../../nodes/LayoutItemNode"

export const INSERT_LAYOUT_COMMAND = createCommand()

export const UPDATE_LAYOUT_COMMAND = createCommand()

export function LayoutPlugin() {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    if (!editor.hasNodes([LayoutContainerNode, LayoutItemNode])) {
      throw new Error(
        "LayoutPlugin: LayoutContainerNode, or LayoutItemNode not registered on editor"
      )
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_LAYOUT_COMMAND,
        template => {
          editor.update(() => {
            const container = $createLayoutContainerNode(template)
            const itemsCount = getItemsCountFromTemplate(template)

            for (let i = 0; i < itemsCount; i++) {
              container.append(
                $createLayoutItemNode().append($createParagraphNode())
              )
            }

            $insertNodeToNearestRoot(container)
            container.selectStart()
          })

          return true
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        UPDATE_LAYOUT_COMMAND,
        ({ template, nodeKey }) => {
          editor.update(() => {
            const container = $getNodeByKey(nodeKey)

            if (!$isLayoutContainerNode(container)) {
              return
            }

            const itemsCount = getItemsCountFromTemplate(template)
            const prevItemsCount = getItemsCountFromTemplate(
              container.getTemplateColumns()
            )

            // Add or remove extra columns if new template does not match existing one
            if (itemsCount > prevItemsCount) {
              for (let i = prevItemsCount; i < itemsCount; i++) {
                container.append(
                  $createLayoutItemNode().append($createParagraphNode())
                )
              }
            } else if (itemsCount < prevItemsCount) {
              for (let i = prevItemsCount; i < itemsCount; i++) {
                const layoutItem = container.getChildAtIndex(i)

                if ($isLayoutItemNode(layoutItem)) {
                  for (const child of layoutItem.getChildren()) {
                    container.insertAfter(child)
                  }
                }
              }
            }

            container.setTemplateColumns(template)
          })

          return true
        },
        COMMAND_PRIORITY_EDITOR
      ), // Structure enforcing transformers for each node type. In case nesting structure is not
      // "Container > Item" it'll unwrap nodes and convert it back
      // to regular content.
      editor.registerNodeTransform(LayoutItemNode, node => {
        const parent = node.getParent()
        if (!$isLayoutContainerNode(parent)) {
          const children = node.getChildren()
          for (const child of children) {
            node.insertBefore(child)
          }
          node.remove()
        }
      }),
      editor.registerNodeTransform(LayoutContainerNode, node => {
        const children = node.getChildren()
        if (!children.every($isLayoutItemNode)) {
          for (const child of children) {
            node.insertBefore(child)
          }
          node.remove()
        }
      })
    )
  }, [editor])

  return null
}

function getItemsCountFromTemplate(template) {
  return template.trim().split(/\s+/).length
}
